import React, { memo, useCallback, useMemo, useState } from 'react'
import SavedLaneForm from 'transfix-form/forms/SavedLaneForm'
import { ISavedLaneFormProps } from 'transfix-form/forms/SavedLaneForm/SavedLaneForm'
import { TSavedLaneSchema } from 'transfix-form/forms/SavedLaneForm/schema'
import { IAppTypes } from 'transfix-form/forms/SavedLaneForm/types'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Maybe } from 'types/graphqlTypes'
import { LaneAlert } from 'apps/marketplace/apiTypes/carrierLaneAlerts'
import { useSavedLanes } from '../hooks/useSavedLanes'
import { getMPSavedLaneSchema } from '../utils'

const APP = IAppTypes.marketplace
const FAILURE_MESSAGE = 'Server error. Contact tech support for assistance.'
const ADD_SUCCESS_MESSAGE = 'Lane info saved successfully'
const EDIT_SUCCESS_MESSAGE = 'Lane updated successfully'

export interface ISavedLaneFormModalProps extends Pick<IFormModalProps, 'onClose'> {
  carrierApplicationId: string
  savedLane?: Maybe<LaneAlert>
  onSuccess: () => void
}

const SavedLaneFormModal = memo<ISavedLaneFormModalProps>(
  ({ carrierApplicationId, onClose, onSuccess, savedLane }) => {
    const { openToast } = useGlobalToast()
    const [loading, setLoading] = useState<boolean>(false)

    const { id: savedLaneId, recipientSubscribers = [] } = savedLane || {}

    // Determine if the form is being used to edit a saved lane
    const isEditing = Boolean(savedLane)

    // Get default form values if form is being used for editing
    const defaultFormValues: ISavedLaneFormProps['defaultValues'] = useMemo(() => {
      if (!isEditing || !savedLane) {
        return undefined
      }
      return getMPSavedLaneSchema(savedLane)
    }, [isEditing, savedLane])

    // Mutations
    const { createSavedLane, updateSavedLane } = useSavedLanes({
      carrierApplicationId,
      savedLaneId,
    })

    const toggleLoading = useCallback(() => {
      setLoading(loading => !loading)
    }, [])

    const showErrorToast = useCallback(() => {
      openToast({ toastMessage: FAILURE_MESSAGE, toastType: 'alert' })
    }, [openToast])

    const showSuccessToast = useCallback(() => {
      openToast({ toastMessage: isEditing ? EDIT_SUCCESS_MESSAGE : ADD_SUCCESS_MESSAGE })
    }, [isEditing, openToast])

    const submitHandler = useCallback(
      async (savedLane: TSavedLaneSchema) => {
        try {
          toggleLoading()
          const response = isEditing
            ? await updateSavedLane(savedLane, recipientSubscribers)
            : await createSavedLane(savedLane)
          if (response) {
            showSuccessToast()
            onSuccess()
          } else {
            showErrorToast()
          }
        } catch {
          showErrorToast()
        } finally {
          toggleLoading()
        }
      },
      [
        isEditing,
        recipientSubscribers,
        toggleLoading,
        updateSavedLane,
        createSavedLane,
        showSuccessToast,
        onSuccess,
        showErrorToast,
      ]
    )

    return (
      <FormModal title='Saved Lane' dataTest='saved-lane-mp-modal' onClose={onClose} open>
        <SavedLaneForm
          app={APP}
          loading={loading}
          carrierApplicationId={carrierApplicationId}
          defaultValues={defaultFormValues}
          onCancel={onClose}
          onSubmit={submitHandler}
        />
      </FormModal>
    )
  }
)

export default SavedLaneFormModal
